import { useEffect, useRef, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { generateClientUUID } from '../services/progressService';
import {
  getBillingPlans, getBillingAccount, beginMockCheckout,
  simulateMockExpiration, cancelMobileMoneyPayment,
} from '../services/billingService';
import { inspectBurkinaPhone, OPERATOR_LABELS } from '../utils/bfMobileNumber';
import BillingConfirmDialog from './BillingConfirmDialog';
import './BillingPanel.css';
import { supportsPremiumOffline, downloadPremiumOffline, getOfflinePremiumStatus } from '../services/offlinePremiumService';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';
}

function formatDuration(item) {
  if (!item) return '—';
  if (item.durationLabel) return item.durationLabel;
  const value = Number(item.durationValue ?? item.months ?? 0);
  const unit = item.durationUnit || (item.months ? 'month' : null);
  if (!value || !unit) return '—';
  if (unit === 'minute') return `${value} minute${value > 1 ? 's' : ''}`;
  return `${value} mois`;
}

const statusLabels = {
  pending: 'En attente',
  succeeded: 'Réussi (fictif)',
  failed: 'Refusé (fictif)',
  cancelled: 'Annulé',
  expired: 'Expiré',
};

export default function BillingPanel({ onLogin, onBackToContent, embedded = false }) {
  const { currentUser, accessProfile, refreshAccessProfile } = useUser();
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyVisibleCount, setHistoryVisibleCount] = useState(5);
  const [paidThrough, setPaidThrough] = useState(null);
  const [accountAccess, setAccountAccess] = useState(null);
  const [planCode, setPlanCode] = useState('');
  const [provider, setProvider] = useState('orange');
  const [phone, setPhone] = useState('');
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [confirmExpiration, setConfirmExpiration] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const requestIdRef = useRef(null);
  const [offlineBusy, setOfflineBusy] = useState(false);
  const [offlineStatus, setOfflineStatus] = useState({ available: false });

  useEffect(() => {
    if (!currentUser?.id) {
      setLoading(false);
      return undefined;
    }
    let mounted = true;
    setLoading(true);
    setError('');
    Promise.all([getBillingPlans(), getBillingAccount()])
      .then(([plans, account]) => {
        if (!mounted) return;
        setSettings(plans);
        setPlanCode((current) => {
          const available = plans.plans || [];
          return available.some((plan) => plan.code === current)
            ? current
            : available[0]?.code || '';
        });
        setHistory(account.payments || []);
        setPaidThrough(account.paidThrough || null);
        setAccountAccess(account.access || null);
        setPayment((account.payments || []).find((item) => item.status === 'pending') || null);
      })
      .catch((failure) => {
        if (mounted) setError(failure.message || 'Impossible de charger les abonnements.');
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [currentUser?.id]);

  useEffect(() => {
    if (!currentUser?.id || !supportsPremiumOffline()) return undefined;
    let mounted = true;
    getOfflinePremiumStatus(currentUser.id).then((status) => {
      if (mounted) setOfflineStatus(status);
    });
    return () => { mounted = false; };
  }, [currentUser?.id, accessProfile?.premiumExpiresAt, accessProfile?.premiumActive]);

  async function downloadForOffline() {
    if (offlineBusy || !currentUser?.id) return;
    setOfflineBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await downloadPremiumOffline();
      setOfflineStatus({ available: true, expiresAt: result.expiresAt });
      setMessage('Contenu Premium téléchargé et licence vérifiée. Vous pouvez tester le mode hors connexion.');
    } catch (failure) {
      setError(failure.message || 'Téléchargement hors connexion impossible.');
    } finally {
      setOfflineBusy(false);
    }
  }

  async function reload() {
    const [account, access] = await Promise.all([
      getBillingAccount(),
      refreshAccessProfile(),
    ]);
    setHistory(account.payments || []);
    setPaidThrough(account.paidThrough || null);
    setAccountAccess(account.access || access || null);
    return account;
  }

  // The server checks this again, atomically, during payment settlement.
  // Online account state is server-authoritative. During an offline session
  // the signed native license (accessProfile.offline) takes precedence over
  // potentially stale account metadata.
  const expiresAt = accessProfile?.offline
    ? accessProfile.premiumExpiresAt
    : paidThrough || accountAccess?.premiumExpiresAt || accessProfile?.premiumExpiresAt;
  const reportedPremium = accessProfile?.offline
    ? accessProfile.premiumActive
    : (accountAccess?.premiumActive ?? accessProfile?.premiumActive);
  const premium = Boolean(
    reportedPremium &&
    (!expiresAt || new Date(expiresAt).getTime() > Date.now())
  );
  // Legacy test-only payments created before the Mobile Money migration must not block the new flow.
  const pending = !premium && payment?.status === 'pending' && Boolean(payment?.provider);
  const plans = settings?.plans || [];
  const selectedPlan = plans.find((plan) => plan.code === planCode);
  const hasMockSuccess = history.some((entry) => entry.status === 'succeeded');
  // This is an allocation hint, not proof of an actual Mobile Money wallet.
  const phoneInspection = inspectBurkinaPhone(phone, provider);
  const validPhone = phoneInspection.valid;
  const compatiblePhone = phoneInspection.compatible;
  const canPurchase = Boolean(
    settings?.enabled &&
    !loading &&
    !busy &&
    !premium &&
    !pending &&
    selectedPlan &&
    compatiblePhone
  );

  // Real gateways usually confirm asynchronously. Poll only while an intent
  // is pending; this also recovers the result after the candidate reconnects.
  // Polling is read-only: the candidate cannot settle an intent.
  useEffect(() => {
    if (!pending || !payment?.id || !settings?.enabled) return undefined;
    let mounted = true;
    let checking = false;
    const watch = async () => {
      if (!mounted || checking) return;
      checking = true;
      try {
        const account = await getBillingAccount();
        if (!mounted) return;
        const latest = (account.payments || []).find((item) => item.id === payment.id);
        if (latest && latest.status !== 'pending') {
          await reload();
          if (!mounted) return;
          setPayment(null);
          setMessage(latest.status === 'succeeded'
            ? 'Confirmation reçue : Premium est actif.'
            : 'Transaction terminée : ' + (statusLabels[latest.status] || latest.status));
        }
      } catch (_) {
        // Temporary disconnection: preserve the pending intent and retry.
        // The server, not the browser, determines its final status.
      } finally {
        checking = false;
      }
    };
    const timer = setInterval(watch, 3000);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  // Only the active payment identity/state should restart the polling loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, payment?.id, settings?.enabled, currentUser?.id]);

  useEffect(() => {
    if (!premium || !expiresAt) {
      setRemainingSeconds(null);
      return undefined;
    }

    const expiry = new Date(expiresAt).getTime();
    if (!Number.isFinite(expiry)) {
      setRemainingSeconds(null);
      return undefined;
    }

    const update = () => {
      setRemainingSeconds(
        Math.max(0, Math.ceil((expiry - Date.now()) / 1000))
      );
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [premium, expiresAt]);

  // Do not rely on the visual countdown to expire access. The server remains
  // authoritative; this timer merely re-reads the real entitlement boundary.
  useEffect(() => {
    if (!premium || !expiresAt || !currentUser?.id) return undefined;
    const expiry = new Date(expiresAt).getTime();
    if (!Number.isFinite(expiry)) return undefined;

    const daily = 24 * 60 * 60 * 1000;
    const untilBoundary = Math.max(1000, expiry - Date.now() + 1200);
    const timeout = setTimeout(() => {
      reload().catch((failure) => {
        console.warn('Rafraîchissement de l’abonnement différé.', failure);
      });
    }, Math.min(daily, untilBoundary));

    return () => clearTimeout(timeout);
  // reload intentionally uses the latest component state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premium, expiresAt, currentUser?.id]);

  async function startPayment() {
    if (!canPurchase) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (!requestIdRef.current) requestIdRef.current = generateClientUUID();
      const response = await beginMockCheckout(planCode, requestIdRef.current, provider, phone);
      setPayment(response.payment);
      requestIdRef.current = null;
      await reload();
    } catch (failure) {
      if (failure.status === 409) await reload().catch(() => {});
      setError(failure.message || 'Création du paiement impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function cancelPayment() {
    if (!pending || busy) return;
    setBusy(true); setError(''); setMessage('');
    try {
      await cancelMobileMoneyPayment(payment.id);
      setPayment(null);
      await reload();
      setMessage('Demande Mobile Money annulée. Aucun abonnement activé.');
    } catch (failure) { setError(failure.message || 'Annulation impossible.'); }
    finally { setBusy(false); }
  }

  async function expireForTest() {
    if (busy || !settings?.enabled || !premium || !confirmExpiration) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await simulateMockExpiration();
      await reload();
      setPayment(null);
      setMessage(result.expiredSubscriptions
        ? 'La période Premium fictive a expiré. Vérifiez le retour aux accès Free.'
        : 'Aucun abonnement fictif actif à expirer. Les autres droits ne sont pas modifiés.');
      setConfirmExpiration(false);
    } catch (failure) {
      setError(failure.message || 'Simulation de l’expiration impossible.');
    } finally {
      setBusy(false);
    }
  }

  if (!currentUser) {
    return (
      <section className="cb-billing cb-billing-anonymous">
        <div className="cb-billing-card cb-billing-intro">
          <div className="cb-billing-eyebrow">CONCOURSBf · Abonnement</div>
          <h1>Accédez aux contenus Premium</h1>
          <p>Connectez-vous pour consulter les formules et vos abonnements.</p>
          {onLogin && (
            <button type="button" onClick={onLogin} className="cb-billing-btn cb-billing-btn--primary">
              Se connecter pour passer Premium <span aria-hidden="true">→</span>
            </button>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className={embedded ? 'cb-billing cb-billing--embedded' : 'cb-billing'}
      aria-label="Gestion des abonnements">
      {embedded ? (
        <div className="cb-billing-embedded-heading">
          <div>
            <p className="cb-billing-kicker">MES AVANTAGES</p>
            <h2>Mon abonnement</h2>
            <p>Consultez vos droits Premium et gérez vos formules d’accès depuis votre profil.</p>
          </div>
          <span className={premium ? 'cb-billing-status is-premium' : 'cb-billing-status'}>
            {premium ? '✦ Premium actif' : 'Accès Free'}
          </span>
        </div>
      ) : <div className="cb-billing-hero">
        <div className="cb-billing-hero-content">
          <div className="cb-billing-eyebrow"><span aria-hidden="true">✦</span> CONCOURSBf · Espace candidat</div>
          <h1>Votre accès Premium</h1>
          <p>Débloquez les cours, les Quiz, les ressources et les simulations pour préparer vos concours à votre rythme.</p>
          <div className="cb-billing-hero-highlights">
            <span>✓ Accès à tous les contenus Premium</span>
            <span>✓ Durée limitée, sans renouvellement automatique</span>
          </div>
        </div>
        <div className="cb-billing-hero-status">
          <span className={premium ? 'cb-billing-status is-premium' : 'cb-billing-status'}>
            <span aria-hidden="true">{premium ? '●' : '○'}</span>
            {premium ? 'Premium actif' : 'Accès Free'}
          </span>
          {premium && expiresAt && <div className="cb-billing-status-date">
            <small>Votre accès est valable jusqu’au</small>
            <strong>{formatDate(expiresAt)}</strong>
            {remainingSeconds !== null && remainingSeconds <= 3600 && (
              <span>
                Expire dans {Math.floor(remainingSeconds / 60)}:
                {String(remainingSeconds % 60).padStart(2, '0')}
              </span>
            )}
          </div>}
        </div>
      </div>}

      {error && <div role="alert" className="cb-billing-notice cb-billing-notice--error">{error}</div>}
      {message && <div role="status" className="cb-billing-notice cb-billing-notice--success">{message}</div>}
      {loading && <div role="status" className="cb-billing-card cb-billing-loading">Chargement de votre abonnement…</div>}

      {supportsPremiumOffline() && premium && (
        <div className="cb-billing-card cb-billing-active" aria-label="Contenus Premium hors connexion">
          <div className="cb-billing-active-copy">
            <p className="cb-billing-kicker">MODE HORS CONNEXION · ANDROID</p>
            <h2>Emporter mes contenus Premium</h2>
            <p>Une connexion au serveur est nécessaire pour télécharger un paquet chiffré
              et sa licence liée à cet appareil. Il reste consultable uniquement
              pendant la durée autorisée, même sans connexion.</p>
            {offlineStatus.available ? (
              <p role="status">✓ Téléchargement validé jusqu'au {formatDate(offlineStatus.expiresAt)}.</p>
            ) : (
              <p>Aucun paquet hors connexion valide n'est disponible pour ce compte.</p>
            )}
            <button type="button" className="cb-billing-btn cb-billing-btn--primary"
              onClick={downloadForOffline} disabled={offlineBusy}>
              {offlineBusy ? 'Préparation du téléchargement…' : 'Télécharger mes contenus Premium'}
            </button>
            <p className="text-xs text-secondary mt-2">Après expiration, le verrouillage est vérifié localement.
              Les documents externes non intégrés au paquet restent indisponibles sans Internet.</p>
          </div>
        </div>
      )}

      {!loading && settings && premium && (
        <div className="cb-billing-card cb-billing-active">
          <div className="cb-billing-active-icon" aria-hidden="true">✓</div>
          <div className="cb-billing-active-copy">
            <p className="cb-billing-kicker">ABONNEMENT EN COURS</p>
            <h2>Vous profitez déjà de Premium</h2>
            <p>
              Votre abonnement est actif{expiresAt ? ` jusqu’au ${formatDate(expiresAt)}` : ''}.
              Il n’est pas nécessaire de payer une deuxième fois : les nouvelles
              souscriptions seront disponibles à son expiration.
            </p>
            {onBackToContent && (
              <button type="button" className="cb-billing-btn cb-billing-btn--primary"
                onClick={onBackToContent}>
                Accéder aux contenus Premium <span aria-hidden="true">→</span>
              </button>
            )}
          </div>
        </div>
      )}

      {!loading && settings && !premium && (
        <div className="cb-billing-card cb-billing-checkout">
          <div className="cb-billing-heading">
            <div>
              <p className="cb-billing-kicker">ÉTAPE 1 · CHOIX DE L’OFFRE</p>
              <h2>Choisissez votre formule</h2>
              <p>Sélectionnez la durée qui vous convient. Pas de renouvellement automatique.</p>
            </div>
            <span className="cb-billing-test-label">MODE TEST · AUCUN DÉBIT RÉEL</span>
          </div>

          {!settings.enabled && (
            <div role="status" className="cb-billing-notice cb-billing-notice--warning">
              <strong>Simulation des paiements désactivée</strong>
              {settings.disabledReason === 'production' ? (
                <p>Les paiements fictifs ne sont pas disponibles en production. Aucun paiement réel n’est proposé pour le moment.</p>
              ) : (
                <>
                  <p>Pour les essais locaux, ajoutez <code>ENABLE_MOCK_PAYMENTS=true</code> dans le fichier <code>.env</code>, puis redémarrez l’API (port 3001).</p>
                  <p>N’activez jamais ce mode dans un environnement commercial.</p>
                </>
              )}
            </div>
          )}

          <fieldset disabled={!settings.enabled || busy || pending} className="cb-billing-plan-fieldset">
            <legend className="cb-billing-legend">Durée de votre abonnement</legend>
            <div className="cb-billing-duration-picker">
              <select id="cb-billing-duration" className="cb-billing-duration-select"
                aria-label="Durée de votre abonnement"
                value={planCode}
                onChange={(event) => {
                  setPlanCode(event.target.value);
                  requestIdRef.current = null;
                }}>
                {plans.some((plan) => plan.isTest) && (
                  <optgroup label="Tests rapides · simulation">
                    {plans.filter((plan) => plan.isTest).map((plan) => (
                      <option key={plan.code} value={plan.code}>{plan.label}</option>
                    ))}
                  </optgroup>
                )}
                {plans.some((plan) => !plan.isTest) && (
                  <optgroup label="Abonnements mensuels">
                    {plans.filter((plan) => !plan.isTest).map((plan) => (
                      <option key={plan.code} value={plan.code}>{plan.label}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              {selectedPlan && (
                <div className="cb-billing-selected-plan" role="status" aria-live="polite">
                  <strong>{selectedPlan.label}</strong>
                  <span>Accès Premium complet{selectedPlan.isTest ? ' · durée de test' : ''}</span>
                  <small>
                    {selectedPlan.isTest ? 'TEST RAPIDE · ' : ''}
                    {selectedPlan.priceXof > 0
                      ? Number(selectedPlan.priceXof).toLocaleString('fr-FR') + ' FCFA (simulation)'
                      : 'Prix à définir dans le back-office'}
                  </small>
                </div>
              )}
            </div>
          </fieldset>

          {!pending && <div className="cb-billing-mobile-money" aria-label="Coordonnées Mobile Money">
            <p className="cb-billing-kicker">ÉTAPE 2 · MOYEN DE PAIEMENT</p>
            <div className="cb-billing-payment-fields">
              <div className="cb-billing-payment-field">
                <label htmlFor="mm-operator">Opérateur Mobile Money</label>
                <select id="mm-operator" value={provider} disabled={busy || !settings.enabled}
                  onChange={(event) => { setProvider(event.target.value); requestIdRef.current = null; }}>
                  <option value="orange">Orange Money Burkina Faso</option>
                  <option value="moov">Moov Money Burkina Faso</option>
                </select>
              </div>
              <div className="cb-billing-payment-field">
                <label htmlFor="mm-phone">Numéro de téléphone burkinabè (8 chiffres)</label>
                <input id="mm-phone" type="tel" inputMode="numeric" autoComplete="off"
                  placeholder="Ex. 70123456" value={phone} maxLength={16}
                  disabled={busy || !settings.enabled} aria-describedby="mm-phone-hint"
                  aria-invalid={phone.length > 0 && !compatiblePhone}
                  onChange={(event) => { setPhone(event.target.value); requestIdRef.current = null; }} />
                <small id="mm-phone-hint" className={phone && !compatiblePhone ? 'cb-billing-phone-hint is-invalid' : 'cb-billing-phone-hint'} role="status">
                  {!phone ? 'Saisissez un numéro fictif à 8 chiffres pour activer le bouton.'
                    : !validPhone ? 'Numéro invalide : saisissez exactement 8 chiffres (ex. 74123456).'
                      : !compatiblePhone
                        ? `Le préfixe ${phoneInspection.prefix} est attribué à ${OPERATOR_LABELS[phoneInspection.operator]}. Choisissez le service correspondant ou un autre numéro.`
                        : phoneInspection.operator
                          ? `Préfixe ${phoneInspection.prefix} attribué à ${OPERATOR_LABELS[phoneInspection.operator]}. Le portefeuille réel n’est pas vérifié en simulation.`
                          : 'Préfixe non identifié dans notre référentiel : le portefeuille devra être vérifié par le prestataire en production.'}
                </small>
              </div>
            </div>
            <p className="cb-billing-payment-disclaimer">
              Simulation uniquement : aucun argent réel, aucun code PIN et aucun OTP.
            </p>
          </div>}

          {!pending ? (
            <div className="cb-billing-checkout-footer">
              <div className="cb-billing-summary">
                <small>VOTRE SÉLECTION</small>
                <strong>{selectedPlan?.label || '—'} de Premium</strong>
                <span>Aucun prélèvement réel</span>
              </div>
              <button type="button" disabled={!canPurchase || !compatiblePhone} onClick={startPayment}
                className="cb-billing-btn cb-billing-btn--primary">
                {busy ? 'Initialisation…' : 'Envoyer la demande Mobile Money'}
                {!busy && <span aria-hidden="true">→</span>}
              </button>
            </div>
          ) : (
            <div className="cb-billing-pending">
              <p className="cb-billing-kicker">ÉTAPE 3 · VALIDATION PAR L'OPÉRATEUR (TEST)</p>
              <h3>Paiement en attente · {formatDuration(payment)}</h3>
              <p>Opérateur : <strong>{payment.provider === 'orange' ? 'Orange Money' : 'Moov Money'}</strong> · Numéro {payment.phoneMasked}</p>
              <p>Référence : <code>{payment.reference}</code></p>
              {payment.simulationMode === 'automatic' ? (
                <p>Demande envoyée. Le prestataire fictif traite automatiquement le paiement en arrière-plan. Le statut se met à jour sans intervention de l’administrateur. Vous pouvez revenir plus tard.</p>
              ) : (
                <p>Mode de test manuel : un agent simule la réponse depuis le back-office « Abonnements ». Vous ne pouvez pas déclarer votre paiement réussi.</p>
              )}
              <p>Aucun code secret, OTP ou somme réelle ne sont demandés. La demande expire après 15 minutes si elle n’est pas confirmée.</p>
              <div className="cb-billing-pending-actions">
                <button type="button" disabled={busy} onClick={async () => {
                  setBusy(true); setError('');
                  try { const account = await reload();
                    const latest = (account.payments || []).find(item => item.id === payment.id);
                    setPayment(latest?.status === 'pending' ? latest : null);
                    if (latest?.status === 'succeeded') setMessage('Confirmation reçue : Premium est actif.');
                    else if (latest?.status && latest.status !== 'pending') setMessage('Transaction terminée : ' + (statusLabels[latest.status] || latest.status));
                  } catch (e) { setError(e.message || 'Actualisation impossible'); }
                  finally { setBusy(false); }
                }} className="cb-billing-btn cb-billing-btn--confirm">Actualiser le statut</button>
                <button type="button" disabled={busy} onClick={cancelPayment}
                  className="cb-billing-btn cb-billing-btn--subtle">Annuler la demande</button>
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && settings?.enabled && premium && hasMockSuccess && (
        <div className="cb-billing-card cb-billing-testing">
          <div>
            <p className="cb-billing-kicker">OUTILS DE VALIDATION</p>
            <h2>Tester l’expiration de Premium</h2>
            <p>Pour vérifier le retour en Free sans attendre la date réelle, vous pouvez simuler la fin de vos abonnements fictifs. Cette action ne touche pas aux droits accordés manuellement.</p>
          </div>
          <button type="button" className="cb-billing-btn cb-billing-btn--outline-warn"
            disabled={busy} onClick={() => setConfirmExpiration(true)}>
            Simuler l’expiration
          </button>
        </div>
      )}

      {!loading && (
        <div className="cb-billing-card cb-billing-history">
          <div className="cb-billing-history-header">
            <div>
              <p className="cb-billing-kicker">SUIVI DE COMPTE</p>
              <h2>Historique des paiements fictifs</h2>
              <p>Vos opérations restent disponibles, sans encombrer votre espace.</p>
              <span className="cb-billing-history-count">
                {history.length} transaction{history.length !== 1 ? 's' : ''}
              </span>
            </div>
            <button type="button"
              className="cb-billing-btn cb-billing-btn--subtle cb-billing-history-toggle"
              aria-expanded={historyOpen}
              aria-controls="cb-billing-history-content"
              onClick={() => {
                setHistoryOpen((opened) => !opened);
                setHistoryVisibleCount(5);
              }}>
              {historyOpen ? 'Masquer l’historique' : 'Voir l’historique'}
            </button>
          </div>
          {historyOpen && (
            <div id="cb-billing-history-content" className="cb-billing-history-content">
              {history.length === 0 ? (
                <div className="cb-billing-empty">Aucune transaction fictive enregistrée.</div>
              ) : (
                <>
                  <div className="cb-billing-table-scroll">
                    <table className="cb-billing-table">
                      <thead><tr><th>Date</th><th>Formule</th><th>Statut</th></tr></thead>
                      <tbody>{history.slice(0, historyVisibleCount).map((item) => (
                        <tr key={item.id}>
                          <td>{formatDate(item.createdAt)}</td>
                          <td>{formatDuration(item)}</td>
                          <td><span className={`cb-billing-payment-status is-${item.status}`}>
                            {statusLabels[item.status] || item.status}
                          </span></td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                  {historyVisibleCount < history.length && (
                    <button type="button"
                      className="cb-billing-btn cb-billing-btn--subtle cb-billing-history-more"
                      onClick={() => setHistoryVisibleCount((count) => count + 5)}>
                      Afficher 5 opérations supplémentaires
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {confirmExpiration && (
        <BillingConfirmDialog busy={busy}
          onCancel={() => setConfirmExpiration(false)}
          onConfirm={expireForTest} />
      )}
    </section>
  );
}
